var $ = require('jquery');
var _ = require('underscore');

var partialListUtils = require('./../../../utils/partialListUtils');

var TorsoListView = require('backbone-torso/modules/ListView');
var tableRowView = require('./TableRowView');
var numberUtils = require('./../../../utils/numberUtils');

var COLUMN_DIRECTIONS = {
  ASC: 'ASC',
  DESC: 'DESC',
  NONE: 'NONE'
};

var NEXT_COLUMN_DIRECTIONS_MAP = {
  ASC: COLUMN_DIRECTIONS.DESC,
  DESC: 'NONE',
  NONE: COLUMN_DIRECTIONS.ASC
};

var DEFAULT_NEXT_COLUMN_DIRECTION = NEXT_COLUMN_DIRECTIONS_MAP.NONE;

/**
 * View for the actual table of BrecTable.
 *
 * @class TableView
 * @constructor
 * @param options {Object} passed in options and the tableState from the parent view.
 *   @param options.tableState {Torso.Cell} the shared state of the widget (filters, search, paging, fetching... etc.).
 */
var TableView = TorsoListView.extend({
  id: function() {
    return $.i18n.prop('widget.table.idPrefix', this.cid);
  },
  tagName: 'table',
  className: 'brec-table brec-table--alternative-row',
  itemContainer: 'brec-table-items',
  itemView: tableRowView,
  events: {
    'click [data-js-sort]': '_updateSorting'
  },
  __itemContext: function() {
    return {
      tableState: this._tableState
    };
  },

  /**
   * @method constructor
   * @param options {Object} passed in options and the tableState from the parent view.
   *   @param options.tableState {Torso.Cell} the shared state of the widget (filters, search, paging, fetching... etc.).
   * @override
   */
  constructor: function(options) {
    _.bindAll(this, 'consumePartialListResponse');
    var defaultOptions = {
      tableState: null
    };
    options = _.defaults({}, options, defaultOptions);
    this._tableState = options.tableState;

    this._tableState.set('loading', true);
    this.listenTo(this._tableState, 'change:loading change:hasData', this.render);

    TorsoListView.apply(this, arguments);
  },

  /**
   * @method prepare
   * @override
   */
  prepare: function() {
    var templateContext = TorsoListView.prototype.prepare.apply(this);
    templateContext.tableState = this._tableState.toJSON();
    templateContext.COLUMN_DIRECTIONS = COLUMN_DIRECTIONS;
    return templateContext;
  },

  /**
   * Starts loading and returns an identifier for tracking this fetch.
   * @method _startLoading
   * @return {Number}
   * @private
   */
  _startLoading: function() {
    this._tableState.set('loading', true);
    return this._getNextFetchId();
  },

  /**
   * Sets loading to false if this is for the last fetch made.
   * @method _completeLoading
   * @param fetchId {Number} the fetch id to check.
   * @private
   */
  _completeLoading: function(fetchId) {
    if (this.get('lastFetchId') === fetchId) {
      this._tableState.set('loading', false);
    }
  },

  /**
   * @method _getNextFetchId
   * @return {Number} a new fetch id to use.  It is also saved as 'lastFetchId' on the view state.
   * @private
   */
  _getNextFetchId: function() {
    var previousFetchId = this.get('lastFetchId') || 0;
    var thisFetchId = previousFetchId + 1;
    this.set('lastFetchId', thisFetchId);
    return thisFetchId;
  },

  /**
   * Helper method for consuming generic partial list responses that contain paging and sorting info along with
   * number of total records on the server and a partial list of records.
   *
   * @method consumePartialListResponse
   * @param response {Object} the response from the server
   *   @param response.list {Object[]} the list of items matching the criteria for the current page of results.
   *   @param [response.fullListSize] {Number} the number of items matching the query ignoring page size.
   *   @param [response.pagingAndSorting] {Object} the generic paging and soring data.
   *     @param [response.pagingAndSorting.pageNumber=0] {Number} the response from the server
   * @param [fetchId] {Number} if set it is checked against the 'lastFetchId' on the view and only updates view state if it the last id.
   */
  consumePartialListResponse: function(response, fetchId) {
    if (fetchId && fetchId === this.get('lastFetchId')) {
      if (response.pagingAndSorting) {
        var serverPageNumber = response.pagingAndSorting.pageNumber;
        var widgetPageNumber = numberUtils.convertZeroToOneBased(serverPageNumber);
        this._tableState.set('page', widgetPageNumber);
      }

      var entriesPerPage = this._tableState.get('entriesPerPage');
      var totalEntries = response.fullListSize;
      if (_.isFinite(totalEntries)) {
        this._tableState.set('totalEntries', totalEntries);
      }

      var totalPages = Math.ceil(totalEntries / entriesPerPage);
      if (_.isFinite(totalPages)) {
        this._tableState.set('totalPages', totalPages);
      }

      this._tableState.set('hasData', response.list && response.list.length > 0);
    }
    return partialListUtils.convertPartialListResponseToList(response);
  },

  /**
   * Helper method for consuming generic partial list responses that contain paging and sorting info along with
   * number of total records on the server and a partial list of records.
   *
   * @method consumeListResponse
   * @param response {Object[]} the response from the server as a list of items.
   * @param [fetchId] {Number} if set it is checked against the 'lastFetchId' on the view and only updates view state if it the last id.
   */
  consumeListResponse: function(response, fetchId) {
    if (fetchId && fetchId === this.get('lastFetchId')) {
      this._tableState.set('hasData', response.length > 0);
    }
    return response;
  },

  /**
   * Update the sorting based on the column clicked.
   * @param event {jQuery.Event} that triggered the sort update.
   * @return {Boolean} false to prevent bubbling of
   * @private
   */
  _updateSorting: function(event) {
    var sortColumnProperty = event.target.getAttribute('data-js-sort');
    var columnSorting = this._tableState.get('columnSorting');
    var newSortOrder = DEFAULT_NEXT_COLUMN_DIRECTION;
    if (columnSorting) {
      var currentSortOrder = columnSorting[sortColumnProperty];
      newSortOrder = NEXT_COLUMN_DIRECTIONS_MAP[currentSortOrder] || DEFAULT_NEXT_COLUMN_DIRECTION;
    }
    // Sort column property might have '.' in it to reference bean properties server-side, this prevents set from misinterpreting them.
    var newColumnSorting = _.extend({}, columnSorting);
    newColumnSorting[sortColumnProperty] = newSortOrder;
    this._tableState.set('columnSorting', newColumnSorting);
    return false;
  }
});

TableView.pagingUpdateEvents = [ '_tableState:change:page', '_tableState:change:entriesPerPage', '_tableState:change:active', '_tableState:change:search', '_tableState:change:columnSorting' ];

module.exports = TableView;
